import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, SlidersHorizontal } from 'lucide-react'
import { useI18n } from '@/i18n'
import { siteConfig } from '@/config/site.config'
import { publicWatches, sortWatches, SORT_OPTIONS } from '@/lib/catalog'
import { useWatches } from '@/hooks/useWatches'
import { Container } from '@/components/layout/Container'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Field'
import { SearchCommand } from '@/components/search/SearchCommand'
import { WatchGrid } from '@/components/catalog/WatchGrid'
import type { SortKey } from '@/types'

/**
 * The home page IS the shop.
 *
 * It opens with the search field and the collection shortcuts, then goes
 * straight into the pieces — the storytelling lives on /maison, where someone
 * who wants it will go looking. A shopper who arrives knowing what they want
 * should reach it in one keystroke, not one scroll.
 */
export default function Home() {
  const { t, tf, tx, formatNumber } = useI18n()
  const { watches, collections, loading } = useWatches()
  const [sort, setSort] = useState<SortKey>('featured')

  const live = useMemo(() => publicWatches(watches), [watches])
  const shown = useMemo(() => sortWatches(live, sort), [live, sort])

  const countIn = (slug: string) => live.filter((w) => w.collection === slug).length

  // 'Best match' means nothing without a query — that option belongs to the
  // search results page, not to a browse-everything grid.
  const sortOptions = SORT_OPTIONS.filter((o) => o.value !== 'relevance').map((o) => ({
    value: o.value,
    label: t(o.labelKey),
  }))

  return (
    <div data-upbo-id="page-home" data-upbo-label="Home page">
      {/* ── Search band ─────────────────────────────────────────────────── */}
      <section
        data-upbo-id="home-search"
        data-upbo-type="section"
        data-upbo-label="Search band"
        className="relative bg-ink"
      >
        {/* The clip lives on the picture, not on the section: the suggestion
            list hangs below the field and must be free to overflow. */}
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={siteConfig.hero.image}
            alt={tx(siteConfig.hero.imageAlt)}
            className="h-full w-full object-cover opacity-45"
          />
          <div className="absolute inset-0 veil" />
        </div>

        <Container size="lg" className="relative">
          <div className="pt-28 pb-12 sm:pt-32 sm:pb-16 flex flex-col items-center text-center gap-5 text-white">
            <span className="eyebrow text-white/55">{tx(siteConfig.tagline)}</span>
            <h1 className="font-display text-[38px] sm:text-[54px] leading-[1.03]">{t('home.searchTitle')}</h1>
            <p className="text-[14px] sm:text-[15px] text-white/70 max-w-xl">
              {t('home.searchLead', { count: formatNumber(live.length) })}
            </p>

            <div className="w-full max-w-2xl mt-3">
              <SearchCommand />
            </div>

            {/* Shortcuts straight into a filtered catalogue. */}
            <div className="flex flex-wrap items-center justify-center gap-2 mt-2">
              <Link
                to="/collection"
                className="h-9 px-4 inline-flex items-center rounded-squircle border border-white/25 text-[11px] uppercase tracking-[0.14em] text-white/85 hover:bg-white/10 hover:text-white transition-colors"
              >
                {t('home.quickAll')}
              </Link>
              {collections.map((c) => (
                <Link
                  key={c.id}
                  to={`/collection?collection=${c.slug}`}
                  className="h-9 px-4 inline-flex items-center gap-2 rounded-squircle border border-white/25 text-[11px] uppercase tracking-[0.14em] text-white/85 hover:bg-white/10 hover:text-white transition-colors"
                >
                  {tf(c, 'name')}
                  <span className="text-white/45">{countIn(c.slug)}</span>
                </Link>
              ))}
            </div>
          </div>
        </Container>
      </section>

      {/* ── The four promises, as a quiet band ─────────────────────────── */}
      <div className="border-b border-border" data-upbo-id="home-promises" data-upbo-label="Promise band">
        <Container size="xl">
          <ul className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-border">
            {siteConfig.marquee.map((key) => (
              <li
                key={key}
                className="py-5 px-4 text-center text-[10px] sm:text-[11px] uppercase tracking-[0.16em] text-text-muted"
              >
                {t(`home.${key}`)}
              </li>
            ))}
          </ul>
        </Container>
      </div>

      {/* ── The pieces, immediately ─────────────────────────────────────── */}
      <section
        data-upbo-id="home-catalogue"
        data-upbo-type="section"
        data-upbo-label="Catalogue"
        className="py-12 sm:py-16"
      >
        <Container size="xl">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div className="flex flex-col gap-2">
              <h2 className="font-display text-[30px] sm:text-[38px] leading-tight">{t('home.gridTitle')}</h2>
              <span className="text-[11px] uppercase tracking-[0.16em] text-text-muted">
                {live.length === 1 ? t('catalog.countOne') : t('catalog.count', { count: live.length })}
              </span>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-[190px]">
                <Select
                  options={sortOptions}
                  value={sort}
                  aria-label={t('catalog.sort')}
                  onChange={(e) => setSort(e.target.value as SortKey)}
                />
              </div>
              <Link to="/collection" className="hidden sm:block">
                <Button variant="outline" icon={<SlidersHorizontal size={15} strokeWidth={1.5} />}>
                  {t('home.gridRefine')}
                </Button>
              </Link>
            </div>
          </div>

          <WatchGrid watches={shown} loading={loading} columns={3} />

          <div className="sm:hidden mt-10">
            <Link to="/collection">
              <Button variant="outline" fullWidth icon={<SlidersHorizontal size={15} strokeWidth={1.5} />}>
                {t('home.gridRefine')}
              </Button>
            </Link>
          </div>
        </Container>
      </section>

      {/* ── One short word about the house, at the bottom where it belongs ── */}
      <section
        data-upbo-id="home-story"
        data-upbo-type="section"
        data-upbo-label="Atelier story"
        className="bg-surface-2 border-t border-border"
      >
        <Container size="xl">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-20 items-center py-14 sm:py-20">
            <div className="overflow-hidden rounded-squircle aspect-[16/10] lg:aspect-[4/3] group">
              <img
                src={siteConfig.story.image}
                alt={tx(siteConfig.story.imageAlt)}
                loading="lazy"
                className="img-zoom h-full w-full object-cover"
              />
            </div>
            <div className="flex flex-col gap-5">
              <span className="eyebrow">{t('home.storyEyebrow')}</span>
              <h2 className="font-display text-[32px] sm:text-[42px] leading-[1.05]">{t('home.storyTitle')}</h2>
              <p className="text-[15px] text-text-muted leading-relaxed max-w-lg">{t('home.storyBody')}</p>
              <Link to="/maison" className="w-fit">
                <Button variant="outline" iconRight={<ArrowRight size={15} strokeWidth={1.5} />}>
                  {t('home.storyCta')}
                </Button>
              </Link>
            </div>
          </div>
        </Container>
      </section>
    </div>
  )
}
